import { connect } from 'react-redux';
import { Tourbook } from '@root/types';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import Button from '@components/shared/V2Button';
import OverflowMenu from '@components/shared/OverflowMenu';
import OverflowMenuItem from '@components/shared/OverflowMenuItem';
import copyToClipboard from '@shared/copyToClipboard';
import { CustomIcon, Notification } from '@components/shared';
import classnames from 'classnames';
import truncateText from '@shared/truncateText';
import actions from '@store/actions/tourbookPage';
import routes from '@root/routes';
import tourbookStyles from './TourbookHeader/Header.module.less';

const mapDispatch = {
  tourbookShare: shareUrl => actions.tourbookShare(shareUrl),
  tourbookCreateEntry: () => actions.tourbookCreateEntry(),
};

const connector = connect(null, mapDispatch);

type Props = {
  tourbook: Tourbook;
  readOnly: boolean;
  onEditClick: () => void;
  onDeleteClick: () => void;
  onDownload: () => void;
  tourbookShare: (shareUrl) => void;
  tourbookCreateEntry: () => void;
  isCollapsed: boolean;
  isLoadingDownload: boolean;
};

const TourbookControls = ({
  tourbook: { name, shareUrl, id },
  readOnly,
  onEditClick,
  onDeleteClick,
  onDownload,
  tourbookShare,
  tourbookCreateEntry,
  isCollapsed,
  isLoadingDownload,
}: Props) => {
  const { t } = useTranslation('tourbook');
  const history = useHistory();

  const onClick = () => {
    copyToClipboard({
      text: shareUrl,
      onSuccess: () => {
        tourbookShare(shareUrl);
        Notification.info({
          title: t('header.copied'),
          text: (
            <a target="_blank" rel="noreferrer" href={shareUrl}>
              {t('header.clickHereToView')}
            </a>
          ),
        });
      },
    });
  };

  const onNewExternalListingClick = () => {
    tourbookCreateEntry();
    history.push(routes.tourbookNewExternalListing(id));
  };

  return (
    <>
      {readOnly && <div className={tourbookStyles.topFiller} />}
      <div
        style={{ background: isCollapsed ? 'white' : 'transparent' }}
        className={classnames([
          tourbookStyles.controlsSection,
          isCollapsed && tourbookStyles.collapsedHeader,
          readOnly && tourbookStyles.readOnly,
        ])}
      >
        <div
          className={classnames([
            tourbookStyles.controlsTitle,
            readOnly && tourbookStyles.readOnly,
          ])}
        >
          {isCollapsed && truncateText(name, 80)}
        </div>
        <div className={tourbookStyles.controlsButtons}>
          {readOnly ? null : (
            <>
              <div className={tourbookStyles.btnContainer}>
                <Button
                  type="secondary"
                  onClick={onNewExternalListingClick}
                  className={tourbookStyles.createButton}
                  icon="plus"
                  iconClass={tourbookStyles.buttonIcon}
                >
                  <span className={tourbookStyles.iconText}>
                    {t('header.createExternalListing')}
                  </span>
                </Button>

                <Button
                  type="secondary"
                  icon="download"
                  className={tourbookStyles.downloadButton}
                  onClick={onDownload}
                  iconClass={tourbookStyles.buttonIcon}
                  loading={isLoadingDownload}
                >
                  <span className={tourbookStyles.iconText}>{t('header.downloadTourbook')}</span>
                </Button>
                <Button
                  type="primary"
                  className={tourbookStyles.shareButton}
                  icon="link"
                  onClick={onClick}
                  iconClass={tourbookStyles.buttonIcon}
                >
                  <span className={tourbookStyles.iconText}>{t('header.copyShareLink')}</span>
                </Button>

                <OverflowMenu
                  overflowClassName={tourbookStyles.overflowMenu}
                  menuWrapperClassName={tourbookStyles.overflowMenuWrapper}
                  overlayClassName={tourbookStyles.overflowDropdown}
                  iconClass={tourbookStyles.overflowIcon}
                  iconType="overflow"
                >
                  <OverflowMenuItem
                    className={classnames(
                      isCollapsed && tourbookStyles.collapsed,
                      tourbookStyles.mobileAndCollapsedOnly,
                    )}
                    onClick={onNewExternalListingClick}
                  >
                    <>
                      <CustomIcon type="plus" />
                      {t('header.createExternalListing')}
                    </>
                  </OverflowMenuItem>

                  <OverflowMenuItem
                    className={classnames(
                      isCollapsed && tourbookStyles.collapsed,
                      tourbookStyles.mobileAndCollapsedOnly,
                    )}
                    onClick={onDownload}
                  >
                    <>
                      <CustomIcon type="download" />
                      {t('header.downloadTourbook')}
                    </>
                  </OverflowMenuItem>

                  <OverflowMenuItem onClick={onEditClick}>
                    <>
                      <CustomIcon type="edit" />
                      {t('header.editTourbook')}
                    </>
                  </OverflowMenuItem>
                  <OverflowMenuItem onClick={onDeleteClick}>
                    <>
                      <CustomIcon type="trash" />
                      {t('header.delete')}
                    </>
                  </OverflowMenuItem>
                </OverflowMenu>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default connector(TourbookControls);
